import { Building2, Phone, Star, Stethoscope } from 'lucide-react'
import type { Service } from '@/api/orderDetails'
import { useLocalization } from '@/contexts/LocalizationContext'
import { collectOrderProviders } from '@/utils/orderProviders'

interface OrderProviderCardProps {
  services: Service[] | null | undefined
}

/**
 * Overview card listing the providers assigned across an order's services.
 * Renders nothing until at least one service has a provider — an unassigned order
 * would otherwise show an empty card above the health profile.
 */
export function OrderProviderCard({ services }: OrderProviderCardProps) {
  const { t, isRTL } = useLocalization()
  const providers = collectOrderProviders(services)
  const listSeparator = isRTL ? '، ' : ', '

  if (providers.length === 0) return null

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      <div className="flex items-center gap-3 border-b border-gray-100 bg-gray-50/50 px-5 py-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100 text-purple-600">
          <Stethoscope className="h-5 w-5" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">{t('orderDetails.provider')}</h3>
          <p className="text-xs text-gray-500">{t('orderDetails.assignedProviders')}</p>
        </div>
      </div>

      <div className="divide-y divide-gray-100">
        {providers.map((provider) => (
          <div key={provider.key} className="flex flex-wrap items-start gap-4 p-5">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-brand-primary text-lg font-bold text-white">
              {provider.name.charAt(0).toUpperCase()}
            </div>

            <div className="min-w-0 flex-1 space-y-1">
              <p className="font-semibold text-gray-900">{provider.name}</p>

              {provider.organizationName && (
                <p className="flex items-center gap-1.5 text-sm text-gray-500">
                  <Building2 className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{provider.organizationName}</span>
                </p>
              )}

              {(provider.averageRating !== null || provider.ratingDisplay) && (
                <p className="flex items-center gap-1 text-sm text-gray-600">
                  <Star className="h-3.5 w-3.5 shrink-0 fill-yellow-500 text-yellow-500" />
                  {provider.averageRating !== null ? (
                    <>
                      <span className="font-medium">{provider.averageRating.toFixed(1)}</span>
                      {provider.totalReviews != null && (
                        <span className="text-gray-500">
                          ({provider.totalReviews} {t('providers.reviews')})
                        </span>
                      )}
                    </>
                  ) : (
                    /* No numeric rating — fall back to the backend's preformatted string. */
                    <span className="font-medium">{provider.ratingDisplay}</span>
                  )}
                </p>
              )}


              {provider.phone && (
                <a
                  href={`tel:${provider.phone}`}
                  className="group inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-brand-primary"
                >
                  <Phone className="h-3.5 w-3.5 shrink-0 text-gray-400 group-hover:text-brand-primary" />
                  <span dir="ltr">{provider.phone}</span>
                </a>
              )}
            </div>

            {provider.serviceNames.length > 0 && (
              <div className="min-w-0 basis-full sm:basis-auto sm:text-end">
                <p className="text-xs uppercase tracking-wide text-gray-400">
                  {t('orderDetails.services')}
                </p>
                <p className="text-sm text-gray-700">{provider.serviceNames.join(listSeparator)}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
